import React, { useState, useEffect, useCallback, useRef } from "react";
import { PropTypes } from "prop-types";
import { generatePath, useNavigate } from "react-router-dom";
import Spinner from "../../../../common/Spinner";
import ErrorComponent from "../../../../common/ErrorComponent";
import HeatMap from "../../../../common/HeatMap";
import NoDataAvailable from "../../../../common/NoDataAvailable";
import { useI18n } from "../../../../i18n/useI18n";
import { createHeatmapWorker } from "./heatmapDataWorker";
import css from "../../circuitsMagneticStyle.less";
import { routeURLs } from "../../../../utils/enums";

const TrendAnalysis = ({
  reportTimeFrame,
  reportHeatMapData,
  reportCircuitMasterTable,
  reportSelectedTrendCircuits,
  reportChartHeatMapStatus,
  reportCurrentPeriodStart,
  reportCurrentPeriodEnd,
  circuitsCallback = () => null,
}) => {
  const { tViews } = useI18n("views");
  const navigate = useNavigate();
  const [chartData, setChartData] = useState();
  const [isRendered, setIsRendered] = useState(false);
  const [maxTime, setMaxTime] = useState();
  const circuitsRef = useRef([]);

  const processHeatMapData = (resData) => {
    let heatmapWorker;
    const lastProcessedTime =
      resData.last_processing_time < reportCurrentPeriodEnd
        ? resData.last_processing_time
        : reportCurrentPeriodEnd;
    setMaxTime(lastProcessedTime);

    const handleWorkerFailure = (message, error) => {
      console.error(message, error);
      setChartData([]);
      setIsRendered(true);
      circuitsCallback(true);
      heatmapWorker?.terminate();
    };

    try {
      heatmapWorker = createHeatmapWorker();
      if (
        heatmapWorker &&
        typeof heatmapWorker.processHeatMapData === "function"
      ) {
        heatmapWorker
          .processHeatMapData(
            resData,
            reportSelectedTrendCircuits,
            reportCircuitMasterTable,
            reportCurrentPeriodStart,
            lastProcessedTime,
            {
              "circuit.unavailableTimeRange": tViews(
                "circuit.unavailableTimeRange",
              ),
            },
          )
          .then((data) => {
            const [circuitsData] = data;
            circuitsRef.current = reportCircuitMasterTable;
            setChartData(circuitsData);
            setIsRendered(true);
            circuitsCallback(true);
            heatmapWorker.terminate();
          })
          .catch((error) => {
            handleWorkerFailure("Heatmap worker processing failed:", error);
          });
      } else {
        handleWorkerFailure(
          "Heatmap worker not properly initialized or processHeatMapData method missing",
        );
      }
    } catch (error) {
      handleWorkerFailure("Error setting up heatmap worker:", error);
    }
  };

  useEffect(() => {
    if (reportHeatMapData?.data && reportSelectedTrendCircuits.length > 0) {
      setIsRendered(false);
      processHeatMapData(reportHeatMapData);
    }
  }, [reportHeatMapData?.data]);

  const onYAxesLabelClick = useCallback(
    (selectedCircuit) => {
      const circuit = circuitsRef.current.find(
        (item) => item.circuit_display_name === selectedCircuit,
      );
      if (circuit) {
        localStorage.setItem(
          "circuit",
          JSON.stringify({
            ...circuit,
            overlay: localStorage.getItem("currentOverlay"),
          }),
        );
        navigate(
          generatePath(routeURLs.circuit360, {
            circuitName: circuit.circuit_display_name.split(":").join("~"),
          }),
        );
      }
    },
    [navigate],
  );

  const hasData =
    isRendered &&
    chartData?.length > 0 &&
    reportChartHeatMapStatus?.resObj?.errorCode === "200" &&
    reportSelectedTrendCircuits.length > 0;
  const hasNoData =
    isRendered &&
    (chartData?.length === 0 || reportSelectedTrendCircuits.length === 0);

  return (
    <div className={css.circuitstrendanalysis}>
      <span className="hbr-type-h2">{tViews("circuit.reportSBTitle")}</span>
      {reportChartHeatMapStatus?.isloading || !isRendered ? (
        <div className="avail-heatmap-loader">
          <Spinner />
        </div>
      ) : hasData ? (
        <div className="avail-heatmap avail-reporting-heatmap">
          <HeatMap
            id="circuit_availability_heatmap"
            data={[...chartData]}
            min={reportCurrentPeriodStart}
            max={maxTime}
            selectedList={reportSelectedTrendCircuits}
            timePeriod={reportTimeFrame}
            onYAxesLabelClick={onYAxesLabelClick}
            callbackFromParent={() => null}
            reporting={true}
          />
        </div>
      ) : hasNoData ? (
        <NoDataAvailable />
      ) : (
        <ErrorComponent
          {...reportChartHeatMapStatus?.resObj}
          width="220px"
          className="large-dashlet-error"
        />
      )}
    </div>
  );
};

TrendAnalysis.propTypes = {
  reportTimeFrame: PropTypes.any,
  reportHeatMapData: PropTypes.object.isRequired,
  reportCircuitMasterTable: PropTypes.arrayOf(PropTypes.object).isRequired,
  reportSelectedTrendCircuits: PropTypes.arrayOf(PropTypes.string).isRequired,
  reportCurrentPeriodStart: PropTypes.any,
  reportCurrentPeriodEnd: PropTypes.any,
  circuitsCallback: PropTypes.func,
  reportChartHeatMapStatus: PropTypes.object.isRequired,
};

export default TrendAnalysis;
